import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Filters from "../layout/Filters";
import { translateText } from "../../utils/translation";
import { getLog, undoActionableRequestLog } from "../../slices/insights";
import {
  ArrowUturnLeftIcon,
  BellAlertIcon,
  BellSlashIcon,
  ExclamationCircleIcon,
  PhotoIcon,
} from "@heroicons/react/24/solid";
import ActionButtons from "../layout/ActionButtons";
import ButtonDropdown from "../forms/ButtonDropdown";
import { Menu } from "@headlessui/react";
import { CSVLink } from "react-csv";
import { capitalise } from "../../utils/text";
import getButtonStyle from "../../utils/styles";
import Button from "../layout/Button";
import Table from "../layout/Table";
import Checkbox from "../forms/Checkbox";
import Badge from "../layout/Badge";
import { getColour } from "../../utils/colours";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";

export default function Log() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(getLog({ dateRange: dateRangeTerm }));
  }, [dispatch, dateRangeTerm]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedLogTypeId, setSelectedLogTypeId] =
    useState<string>("actionable");
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { log } = useAppSelector((state) => state.insights);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectRequest(requestId: string) {
    const updatedSelectedRequests = [...selectedRequests];
    if (!updatedSelectedRequests.includes(requestId)) {
      updatedSelectedRequests.push(requestId);
    } else {
      updatedSelectedRequests.splice(
        updatedSelectedRequests.indexOf(requestId),
        1,
      );
    }
    setSelectedRequests(updatedSelectedRequests);
  }

  function selectAllRequests(requestId: any) {
    const currentRequestIds = actionableLogData.map((request) => request.id);

    // If all are selected, unselect all
    if (
      selectedRequests.length > 0 &&
      selectedRequests.length === currentRequestIds.length
    ) {
      setSelectedRequests([]);
      return;
    }

    // Otherwise, select all
    const requestIds = actionableLogData.map((request) => request.id);
    setSelectedRequests(requestIds);
  }

  async function undoActionableRequests(e: any, args: any[]) {
    e.preventDefault();
    const [logIds] = args;
    const res: any = await dispatch(undoActionableRequestLog({ logIds }));
    if (res.payload.status === 200) {
      setSelectedRequests([]);
      dispatch(
        addAlert(generateAlertText("undo_log", language), "success", 3000),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("undo_log_error", language), "danger", 3000),
      );
    }
  }

  function getSelectedRequestsText(numRequests: number) {
    if (language === "EN") {
      if (numRequests === 1) return `${numRequests} request selected`;
      return `${numRequests} requests selected`;
    }
    if (language === "FR") {
      if (numRequests === 1) return `${numRequests} demande sélectionnée`;
      return `${numRequests} demandes sélectionnées`;
    }
    if (language === "IT") {
      if (numRequests === 1) return `${numRequests} richiesta selezionata`;
      return `${numRequests} richieste selezionate`;
    }
    if (numRequests === 1) return `${numRequests} request selected`;
    return `${numRequests} requests selected`;
  }

  const logData = log.data;
  const actionableLogData = logData === null ? [] : logData.actionable_requests;
  const nonActionableLogData =
    logData === null ? [] : logData.non_actionable_requests;
  const contentLogData = logData === null ? [] : logData.content;

  const tabs = [
    {
      id: "actionable",
      name: capitalise(translateText("actionable_requests", language)),
      icon: <BellAlertIcon className="h-5 w-5 mr-1" aria-hidden="true" />,
    },
    {
      id: "non_actionable",
      name: translateText("non_actionable_requests", language),
      icon: <BellSlashIcon className="h-5 w-5 mr-1" aria-hidden="true" />,
    },
    {
      id: "content",
      name: translateText("content", language),
      icon: <PhotoIcon className="h-5 w-5 mr-1" aria-hidden="true" />,
    },
  ];

  let exportData = [];
  if (log.export !== null) {
    if (selectedLogTypeId === "actionable") {
      exportData = log.export.actionable_requests;
    } else if (selectedLogTypeId === "non_actionable") {
      exportData = log.export.non_actionable_requests;
    } else if (selectedLogTypeId === "content") {
      exportData = log.export.content;
    }
  }

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("log_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <ButtonDropdown header={translateText("download_data", language)}>
                <>
                  <Menu.Item>
                    <CSVLink
                      className="bg-white text-black/70 hover:text-black hover:bg-light/20 text-xs block px-4 py-2"
                      data={exportData}
                      filename={`audico_log_data_${selectedLogTypeId}_export.csv`}
                    >
                      CSV
                    </CSVLink>
                  </Menu.Item>
                </>
              </ButtonDropdown>
            }
          />
        }
      />

      {/* Loading spinner */}
      {log.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              queryParams={[["dateRange", dateRangeTerm]]}
              handlers={{
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={log.loading}
              baseUrl="/insights/log"
              showDateRange={true}
              showFilters={false}
              showSearchBar={false}
            />
            {logData !== null ? (
              <div className="mx-1 mb-10">
                {/* Tab navigation */}
                <div className="pb-10">
                  <div className="sm:hidden">
                    <label htmlFor="tabs" className="sr-only">
                      Select a tab
                    </label>
                    <select
                      id="tabs"
                      name="tabs"
                      className="block w-full rounded-md border-gray-300 text-black focus:border-primary focus:ring-primary text-sm"
                      defaultValue={
                        tabs.filter((tab) => tab.id === "actionable")[0].name
                      }
                      onChange={(e) => setSelectedLogTypeId(e.target.value)}
                    >
                      {tabs.map((tab) => (
                        <option key={tab.id} value={tab.id}>
                          {tab.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="hidden sm:block">
                    <nav className="flex space-x-4" aria-label="Tabs">
                      {tabs.map((tab) => (
                        <a
                          key={tab.name}
                          href="#!"
                          onClick={() => setSelectedLogTypeId(tab.id)}
                          className={`${
                            tab.id === selectedLogTypeId
                              ? "bg-primary text-white"
                              : "text-secondary hover:text-black hover:bg-white"
                          } inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium`}
                          aria-current={
                            selectedLogTypeId === tab.id ? "page" : undefined
                          }
                        >
                          {tab.icon}
                          {tab.name}
                        </a>
                      ))}
                    </nav>
                  </div>
                </div>

                {/* Tab content */}
                {/* Actionable request log */}
                {selectedLogTypeId === "actionable" && (
                  <>
                    {actionableLogData.length > 0 ? (
                      <>
                        {/* Table actions */}
                        {selectedRequests.length > 0 && (
                          <div>
                            <p className="text-sm text-granite italic">
                              {getSelectedRequestsText(selectedRequests.length)}
                            </p>
                            <div
                              className={`mt-2 mb-4 grid grid-cols-1 space-y-2 md:space-y-0 gap-x-2`}
                            >
                              {/* Undo actions */}
                              <Button
                                type="button"
                                className={`${getButtonStyle(
                                  "solid",
                                  "warning",
                                )}`}
                                onClick={undoActionableRequests}
                                onClickArgs={[selectedRequests]}
                              >
                                <ArrowUturnLeftIcon
                                  className="w-5 h-5 mr-2"
                                  aria-hidden="true"
                                />
                                {translateText("undo", language)}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Table */}
                        <Table
                          className={null}
                          header={
                            <tr>
                              <th className="px-2 py-3">
                                <Checkbox
                                  id="select-all"
                                  onChange={selectAllRequests}
                                  checked={
                                    selectedRequests.length > 0 &&
                                    selectedRequests.length ===
                                      actionableLogData.length
                                  }
                                />
                              </th>
                              <th className="px-2 py-3">
                                {translateText("created", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("activity", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("associated_user", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("request", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("details", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("quantity", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("room", language)}
                              </th>
                            </tr>
                          }
                          body={actionableLogData.map((request) => (
                            <tr
                              key={request.id}
                              className={`${
                                selectedRequests.includes(request.id) &&
                                "bg-light/20"
                              } hover:bg-light/20`}
                              onClick={() => selectRequest(request.id)}
                            >
                              <td className="border-r border-light/50 px-2 py-3">
                                <Checkbox
                                  id={request.id}
                                  onChange={selectRequest}
                                  checked={selectedRequests.includes(
                                    request.id,
                                  )}
                                />
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.datetime}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.activity}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.associated_user}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.request}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.details}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.quantity}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap">
                                {request.room}
                              </td>
                            </tr>
                          ))}
                          showPagination={false}
                          paginationHasPrevious={false}
                          paginationHasNext={false}
                          paginationCurrentPage={1}
                          paginationTotalPages={1}
                          paginationTotalObjects={1}
                          paginationQueryParams={[]}
                        />
                      </>
                    ) : (
                      <NoContent
                        text={translateText("not_enough_data", language)}
                        subtext={translateText(
                          "not_enough_data_log_non_actionable_subtext",
                          language,
                        )}
                        icon={
                          <ExclamationCircleIcon
                            className="mx-auto h-12 w-12 text-secondary animate-bounce"
                            aria-hidden="true"
                          />
                        }
                      />
                    )}
                  </>
                )}

                {/* Non-actionable request log */}
                {selectedLogTypeId === "non_actionable" && (
                  <>
                    {/* Table */}
                    {nonActionableLogData.length > 0 ? (
                      <Table
                        header={
                          <tr>
                            <th className="px-2 py-3">
                              {translateText("created", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("activity", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("request", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("details", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("quantity", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("room", language)}
                            </th>
                          </tr>
                        }
                        body={nonActionableLogData.map((request) => (
                          <tr key={request.id} className="hover:bg-light/20">
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.datetime}
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.activity}
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.request}
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.details}
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.quantity}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap">
                              {request.room}
                            </td>
                          </tr>
                        ))}
                        showPagination={false}
                        paginationHasPrevious={false}
                        paginationHasNext={false}
                        paginationCurrentPage={1}
                        paginationTotalPages={1}
                        paginationTotalObjects={1}
                        paginationQueryParams={[]}
                      />
                    ) : (
                      <NoContent
                        text={translateText("not_enough_data", language)}
                        subtext={translateText(
                          "not_enough_data_log_non_actionable_subtext",
                          language,
                        )}
                        icon={
                          <ExclamationCircleIcon
                            className="mx-auto h-12 w-12 text-secondary animate-bounce"
                            aria-hidden="true"
                          />
                        }
                      />
                    )}
                  </>
                )}

                {/* Content log */}
                {selectedLogTypeId === "content" && (
                  <>
                    {/* Table */}
                    {contentLogData.length > 0 ? (
                      <Table
                        header={
                          <tr>
                            <th className="px-2 py-3">
                              {translateText("created", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("type", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("activity", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("associated_user", language)}
                            </th>
                            <th className="px-2 py-3">
                              {translateText("change", language)}
                            </th>
                          </tr>
                        }
                        body={contentLogData.map((request) => (
                          <tr key={request.id} className="hover:bg-light/20">
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.datetime}
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              <Badge
                                text={request.type[1]}
                                bgColour={getColour(request.type[0])}
                                textColour={`${getColour(
                                  request.type[0],
                                )}-dark`}
                                ringColour={`${getColour(
                                  request.type[0],
                                )}-dark`}
                              />
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.activity}
                            </td>
                            <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                              {request.associated_user}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap">
                              {request.change}
                            </td>
                          </tr>
                        ))}
                        showPagination={false}
                        paginationHasPrevious={false}
                        paginationHasNext={false}
                        paginationCurrentPage={1}
                        paginationTotalPages={1}
                        paginationTotalObjects={1}
                        paginationQueryParams={[]}
                      />
                    ) : (
                      <NoContent
                        text={translateText("not_enough_data", language)}
                        subtext={translateText(
                          "not_enough_data_log_content_subtext",
                          language,
                        )}
                        icon={
                          <ExclamationCircleIcon
                            className="mx-auto h-12 w-12 text-secondary animate-bounce"
                            aria-hidden="true"
                          />
                        }
                      />
                    )}
                  </>
                )}
              </div>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("not_enough_data", language)}
                subtext={translateText("not_enough_data_log_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
